import React, { useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Card, Text } from '@zillow/constellation-10';

import { useSelector } from '../../../selectors/useSelector';

import { ZRMOverview } from '../ZRMOverview';
import { NoSearchResults } from '../NoSearchResults';

import GroupablePropertiesViewList from '../GroupablePropertiesViewList';

import type { AccountViewListingGroupDto, ListingCountDto } from '@zg-rentals/ts-rental-manager-api-client';

export type Props = {
  listingGroups: Array<AccountViewListingGroupDto>;
  listingCount?: ListingCountDto;
  isPending: boolean;
  isFetching: boolean;
  onRemoveSearchTextClick?: (value: string) => void;
};

const GroupablePropertiesDisplay = ({
  listingGroups = [],
  listingCount,
  isPending,
  isFetching,
  onRemoveSearchTextClick,
}: Props) => {
  const { user, propertiesFilters } = useSelector(
    (state) => ({
      user: state.user,
      propertiesFilters: state.properties.propertiesFilters,
    }),
    shallowEqual,
  );

  const [didPropertiesLoad, setDidPropertiesLoad] = useState(false);

  const isLoading = isPending || isFetching;
  const showTimedOutDisplay = !didPropertiesLoad && !isLoading;
  const showArchived = propertiesFilters.includeArchived;
  const hasAnyArchivedListings = (listingCount?.archived || 0) > 0;

  const handleRemoveSearchTextClick = (value: string) => {
    if (onRemoveSearchTextClick) {
      onRemoveSearchTextClick(value);
    }
  };

  useEffect(() => {
    if (!isLoading) {
      setDidPropertiesLoad(true);
    }
  }, [isLoading]);

  const toggleArchivedView = () => {
    // This will be handled by the parent component via propertiesFilters update
    // The component will automatically re-fetch when propertiesFilters change
  };

  const renderTimedOutDisplay = () => (
    <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginTop: '1200' }}>
      <Text
        css={{
          textAlign: 'center',
          padding: { base: '0 {spacing.400}', lg: '0' },
          marginBottom: '800',
          marginInline: '400',
        }}
      >
        We are having trouble retrieving your properties at this time. Please refresh the page or check back soon.
      </Text>
    </Box>
  );

  const renderNoListingsFound = () => {
    if (propertiesFilters.searchText) {
      return <NoSearchResults showArchived={showArchived} onRemoveSearchTextClick={handleRemoveSearchTextClick} />;
    }

    if (showArchived) {
      return (
        <Text css={{ textAlign: 'center', padding: { base: '0 {spacing.400}', lg: '0' }, marginBottom: '800' }}>
          You don&apos;t have any archived properties.
        </Text>
      );
    }

    if (user.features.canAddListing) {
      return (
        <Box data-testid="GroupablePropertiesDisplay-zrm-overview">
          <ZRMOverview hasAnyArchivedListings={hasAnyArchivedListings} handleShowArchivedClick={toggleArchivedView} />
        </Box>
      );
    }

    return (
      <Text css={{ textAlign: 'center', padding: { base: '0 {spacing.400}', lg: '0' }, marginBottom: '800' }}>
        No properties found.
      </Text>
    );
  };

  const renderListingsDisplay = () => (
    <React.Fragment>
      {listingGroups.length > 0 ? (
        <GroupablePropertiesViewList listings={listingGroups} />
      ) : (
        <Card
          outlined={false}
          elevated={false}
          css={{
            marginBlock: '1200',
            backgroundColor: 'transparent',
            textAlign: 'center',
            lg: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around' },
          }}
        >
          {renderNoListingsFound()}
        </Card>
      )}
    </React.Fragment>
  );

  return (
    <Box data-testid="GroupablePropertiesDisplay">
      {showTimedOutDisplay ? renderTimedOutDisplay() : renderListingsDisplay()}
    </Box>
  );
};

export { GroupablePropertiesDisplay };
export default GroupablePropertiesDisplay;
